import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  DollarSign, TrendingUp, Wallet, CreditCard, ArrowUpRight, ArrowDownLeft,
  Clock, CheckCircle, AlertCircle, Loader2, Banknote, Receipt, PiggyBank,
  ChevronRight, Calendar, Star, Briefcase, Download, Filter, RefreshCw,
  Zap, Percent, Info, Users, Crown, Building, Truck, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Revenue Split Visual
function RevenueSplitCard() {
  const splits = [
    { label: 'Provider', percent: 30, color: 'bg-blue-500', description: 'Service provider earnings' },
    { label: 'Owner', percent: 40, color: 'bg-green-500', description: 'Your revenue share' },
    { label: 'Admin', percent: 8, color: 'bg-purple-500', description: 'Admin who assigned job' },
    { label: 'Operations', percent: 22, color: 'bg-orange-500', description: 'Platform operations' }
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Percent className="w-5 h-5 text-indigo-600" />
          Revenue Split Breakdown
        </CardTitle>
        <CardDescription>How each service payment is distributed</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Visual Bar */}
        <div className="h-8 rounded-lg overflow-hidden flex mb-4">
          {splits.map((split, idx) => (
            <div 
              key={idx}
              className={`${split.color} flex items-center justify-center text-white text-xs font-bold`}
              style={{ width: `${split.percent}%` }}
            >
              {split.percent}%
            </div>
          ))}
        </div>
        
        {/* Legend */}
        <div className="grid grid-cols-2 gap-3">
          {splits.map((split, idx) => (
            <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <div className={`w-3 h-3 rounded-full ${split.color}`} />
              <div>
                <p className="text-sm font-medium text-gray-900">{split.label}</p>
                <p className="text-xs text-gray-500">{split.description}</p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Additional Info */}
        <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-800">
            <strong>Tips:</strong> 100% go to providers • 
            <strong> Parts Markup:</strong> 16.8% + 7% tax • 
            <strong> Early Payout Fee:</strong> 5% to owner
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// Transaction Row Component
function TransactionRow({ transaction }) {
  const isPositive = transaction.amount > 0;
  
  return (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPositive ? 'bg-green-100' : 'bg-red-100'}`}>
          {isPositive ? (
            <ArrowDownLeft className="w-5 h-5 text-green-600" />
          ) : (
            <ArrowUpRight className="w-5 h-5 text-red-600" />
          )}
        </div>
        <div>
          <p className="font-medium text-gray-900">{transaction.description || transaction.transaction_type}</p>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Calendar className="w-3 h-3" />
            {new Date(transaction.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>
      <div className="text-right">
        <p className={`font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          {isPositive ? '+' : ''}${transaction.amount?.toFixed(2) || '0.00'}
        </p>
        <Badge variant="outline" className="text-xs">{transaction.transaction_type}</Badge>
      </div>
    </div>
  );
}

// Main Owner Earnings Component
export default function OwnerEarningsTab() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ownerData, setOwnerData] = useState(null);
  const [allPayouts, setAllPayouts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ownerRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/payouts/owner/balance`, { headers: getAuthHeaders(), withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/payouts/admin/all-payouts`, { headers: getAuthHeaders(), withCredentials: true }).catch(() => ({ data: { payouts: [], stats: {} } }))
      ]);
      
      setOwnerData(ownerRes.data);
      setAllPayouts(payoutsRes.data);
    } catch (error) {
      console.error('Failed to fetch owner data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-10 h-10 text-yellow-600 animate-spin" />
      </div>
    );
  }

  const ownerBalance = ownerData?.owner_balance || 0;
  const feesReceived = ownerData?.early_payout_fees_received || 0;
  const revenueShare = ownerData?.revenue_share_40_percent || 0;
  const totalBalance = ownerData?.summary?.total_balance || (ownerBalance + feesReceived + revenueShare);
  const payoutStats = allPayouts?.stats || {};

  return (
    <div className="space-y-6" data-testid="owner-earnings-tab">
      {/* Main Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Balance - Main Card */}
        <Card className="md:col-span-2 bg-gradient-to-br from-yellow-400 to-amber-500 text-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Crown className="w-5 h-5" />
                  <p className="text-yellow-100 text-sm font-medium">Owner Total Balance</p>
                </div>
                <p className="text-4xl font-bold">${totalBalance.toFixed(2)}</p>
              </div>
              <Button 
                variant="secondary"
                className="bg-white/20 hover:bg-white/30 text-white border-0"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            
            {/* Balance Breakdown */}
            <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-yellow-300/30">
              <div>
                <p className="text-yellow-200 text-xs">40% Revenue Share</p>
                <p className="font-bold text-lg">${revenueShare.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-yellow-200 text-xs">Early Payout Fees</p>
                <p className="font-bold text-lg">${feesReceived.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-yellow-200 text-xs">Direct Balance</p>
                <p className="font-bold text-lg">${ownerBalance.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="space-y-4">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-green-700">Fees Collected</p>
                  <p className="text-xl font-bold text-green-800">${payoutStats.total_fees_collected?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                  <Banknote className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-blue-700">Total Paid Out</p>
                  <p className="text-xl font-bold text-blue-800">${payoutStats.total_paid_out?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Revenue Split Card */}
      <RevenueSplitCard />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="transactions" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            <span className="hidden sm:inline">Transactions</span>
          </TabsTrigger>
          <TabsTrigger value="provider-payouts" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Provider Payouts</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Sources */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Revenue Sources
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-gray-900">Service Revenue (40%)</p>
                      <p className="text-xs text-gray-500">From completed services</p>
                    </div>
                  </div>
                  <span className="font-bold text-green-600">${revenueShare.toFixed(2)}</span>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="font-medium text-gray-900">Early Payout Fees (5%)</p>
                      <p className="text-xs text-gray-500">From Same Day Pay requests</p>
                    </div>
                  </div>
                  <span className="font-bold text-yellow-600">${feesReceived.toFixed(2)}</span>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-gray-900">Parts Markup (16.8%)</p>
                      <p className="text-xs text-gray-500">On parts sold</p>
                    </div>
                  </div>
                  <span className="font-bold text-purple-600">Included</span>
                </div>
              </CardContent>
            </Card>

            {/* Balance Routing Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-600" />
                  Balance Routing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 mb-2"><strong>Automatic Routing:</strong></p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• 30% → Provider immediately on completion</li>
                    <li>• 40% → Your owner balance</li>
                    <li>• 8% → Admin who assigned (if any)</li>
                    <li>• 22% → Operations reserve</li>
                    <li>• Leftover amounts → Your balance</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800 mb-2"><strong>Same Day Pay (5% Fee):</strong></p>
                  <p className="text-sm text-green-700">
                    When providers request early payouts, they receive 95% and you automatically receive the 5% fee.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <span>Recent Transactions</span>
                <Badge variant="outline">{ownerData?.recent_transactions?.length || 0} transactions</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 max-h-96 overflow-y-auto">
              {(!ownerData?.recent_transactions || ownerData.recent_transactions.length === 0) ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">No transactions yet</p>
                </div>
              ) : (
                ownerData.recent_transactions.map((tx, idx) => (
                  <TransactionRow key={tx.transaction_id || idx} transaction={tx} />
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Provider Payouts Tab */}
        <TabsContent value="provider-payouts">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <span>All Provider Payouts</span>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-800">
                    ${payoutStats.total_paid_out?.toFixed(2) || '0.00'} paid
                  </Badge>
                  <Badge className="bg-yellow-100 text-yellow-800">
                    {payoutStats.pending_count || 0} pending
                  </Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 max-h-96 overflow-y-auto">
              {(!allPayouts?.payouts || allPayouts.payouts.length === 0) ? (
                <div className="text-center py-12 text-gray-500">
                  <Banknote className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">No provider payouts yet</p>
                </div>
              ) : (
                allPayouts.payouts.map((payout) => (
                  <div 
                    key={payout.payout_id} 
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        payout.status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'
                      }`}>
                        {payout.status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-yellow-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{payout.provider_name}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(payout.requested_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">${payout.payout_amount?.toFixed(2)}</p>
                      <p className="text-xs text-green-600">Fee: +${payout.fee_amount?.toFixed(2)}</p>
                      <Badge className={`text-xs ${
                        payout.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {payout.status}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
